import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, IconButton, Tooltip, Chip,
  CircularProgress, Alert, InputAdornment, TableSortLabel,
} from '@mui/material';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import Search from '@mui/icons-material/Search';
import Language from '@mui/icons-material/Language';
import EditableTable from '../components/common/EditableTable';
import { suppliersAPI } from '../services/api';

const conditionColumns = [
  { field: 'description', header: 'Descripcion', width: '25%' },
  { field: 'percentage', header: '% del Total', type: 'number' },
  { field: 'milestone', header: 'Hito' },
  { field: 'delay_days', header: 'Dias retraso', type: 'number' },
  { field: 'delay_from', header: 'Desde' },
];

const conditionFields = [
  { field: 'description', label: 'Descripcion', required: true, fullWidth: true },
  { field: 'percentage', label: '% del total', type: 'number', required: true, defaultValue: '' },
  { field: 'milestone', label: 'Hito de referencia', type: 'select', required: true, defaultValue: 'pedido', options: [
    { value: 'pedido', label: 'Pedido' },
    { value: 'entrega', label: 'Entrega' },
    { value: 'factura', label: 'Factura' },
    { value: 'montaje', label: 'Fin montaje' },
    { value: 'puesta_en_marcha', label: 'Puesta en marcha' },
    { value: 'recepcion_provisional', label: 'Recepcion provisional' },
  ]},
  { field: 'delay_days', label: 'Dias de retraso', type: 'number', defaultValue: 0 },
  { field: 'delay_from', label: 'Contar desde', type: 'select', defaultValue: 'milestone', options: [
    { value: 'milestone', label: 'Fecha del hito' },
    { value: 'invoice_date', label: 'Fecha factura' },
    { value: 'delivery_date', label: 'Fecha entrega' },
  ]},
  { field: 'order', label: 'Orden', type: 'number', defaultValue: 1 },
];

const EMPTY_FORM = {
  code: '', name: '', cif: '', address: '', city: '', province: '',
  country: 'Spain', category: '', description: '', web: '',
  contact_name: '', contact_email: '', contact_phone: '', notes: '',
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedSuppliers = [...suppliers].sort((a, b) => {
    const va = a[sortField] ?? '';
    const vb = b[sortField] ?? '';
    const cmp = String(va).localeCompare(String(vb), 'es', { numeric: true });
    return sortDir === 'desc' ? -cmp : cmp;
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQ(searchInput), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadSuppliers(searchQ);
  }, [searchQ]);

  useEffect(() => {
    if (selectedSupplier) {
      loadConditions(selectedSupplier.id);
    } else {
      setConditions([]);
    }
  }, [selectedSupplier]);

  const loadSuppliers = async (q = '') => {
    try {
      setLoading(true);
      const resp = await suppliersAPI.list(q || undefined);
      setSuppliers(resp.data);
      setError(null);
    } catch (err) {
      console.error('Error loading suppliers:', err);
      setError('Error al cargar los proveedores.');
    } finally {
      setLoading(false);
    }
  };

  const loadConditions = async (supplierId) => {
    try {
      const resp = await suppliersAPI.listConditions(supplierId);
      setConditions(resp.data);
    } catch (err) {
      console.error('Error loading conditions:', err);
      setConditions([]);
    }
  };

  const nextCode = (prefix, list) => {
    const nums = list
      .map(x => x.code)
      .filter(c => c && c.startsWith(prefix))
      .map(c => parseInt(c.replace(prefix, ''), 10))
      .filter(n => !isNaN(n));
    const max = nums.length > 0 ? Math.max(...nums) : 0;
    return `${prefix}${String(max + 1).padStart(3, '0')}`;
  };

  const handleOpenNew = () => {
    setForm({ ...EMPTY_FORM, code: nextCode('PROV-', suppliers) });
    setFormError('');
    setEditingSupplier(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (supplier) => {
    setForm({ ...EMPTY_FORM, ...supplier });
    setFormError('');
    setEditingSupplier(supplier);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setFormError('');
      if (editingSupplier) {
        await suppliersAPI.update(editingSupplier.id, form);
      } else {
        await suppliersAPI.create(form);
      }
      await loadSuppliers(searchQ);
      setDialogOpen(false);
      setError(null);
    } catch (err) {
      console.error('Error saving supplier:', err);
      const msg = err.response?.data?.detail || 'Error al guardar el proveedor.';
      setFormError(msg);
    }
  };

  const handleDeleteRequest = (id) => {
    const supplier = suppliers.find((s) => s.id === id);
    setConfirmDelete({ id, name: supplier?.name || '' });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { id } = confirmDelete;
    setConfirmDelete(null);
    try {
      await suppliersAPI.delete(id);
      if (selectedSupplier?.id === id) setSelectedSupplier(null);
      await loadSuppliers(searchQ);
    } catch (err) {
      console.error('Error deleting supplier:', err);
      const msg = err.response?.data?.detail || 'Error al eliminar el proveedor.';
      setError(msg);
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const condHandlers = {
    onAdd: async (data) => {
      try {
        await suppliersAPI.createCondition(selectedSupplier.id, data);
        await loadConditions(selectedSupplier.id);
      } catch (err) { console.error('Error adding condition:', err); }
    },
    onUpdate: async (id, data) => {
      try {
        await suppliersAPI.updateCondition(id, data);
        await loadConditions(selectedSupplier.id);
      } catch (err) { console.error('Error updating condition:', err); }
    },
    onDelete: async (id) => {
      try {
        await suppliersAPI.deleteCondition(id);
        await loadConditions(selectedSupplier.id);
      } catch (err) { console.error('Error deleting condition:', err); }
    },
  };

  const totalPct = conditions.reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold">Proveedores</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenNew}>Nuevo Proveedor</Button>
      </Box>

      {/* Condiciones del proveedor seleccionado — panel superior */}
      {selectedSupplier && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">
              Condiciones de Pago: <strong>{selectedSupplier.name}</strong>
            </Typography>
            <Button size="small" onClick={() => setSelectedSupplier(null)}>Cerrar</Button>
          </Box>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Condiciones por defecto. Se copian automaticamente al asignar este proveedor a un proyecto (editables por proyecto).
          </Typography>
          <EditableTable
            title="Condiciones de Pago"
            columns={conditionColumns}
            rows={conditions}
            dialogFields={conditionFields}
            emptyMessage="No hay condiciones definidas. Haz clic en Anadir."
            {...condHandlers}
          />
          <Box sx={{ mt: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">Total: <strong>{totalPct}%</strong></Typography>
            {totalPct !== 100 && totalPct > 0 && <Chip label="Los porcentajes no suman 100%" color="warning" size="small" />}
            {totalPct === 100 && <Chip label="OK - 100%" color="success" size="small" />}
          </Box>
        </Paper>
      )}

      {/* Search bar */}
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar por nombre, descripcion, contacto, email, ciudad, web..."
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#c62828' }}>
                {[['code','Codigo'],['name','Proveedor'],['conditions_summary','Condiciones'],['description','Descripcion'],['contact_name','Contacto'],['contact_email','Email'],['contact_phone','Telefono']].map(([field, label]) => (
                  <TableCell key={field} sx={{ fontWeight: 'bold', color: '#fff' }} sortDirection={sortField === field ? sortDir : false}>
                    <TableSortLabel active={sortField === field} direction={sortField === field ? sortDir : 'asc'} onClick={() => handleSort(field)}>{label}</TableSortLabel>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }}>Web</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#fff' }} align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {searchInput ? `Sin resultados para "${searchInput}"` : 'No hay proveedores registrados'}
                  </TableCell>
                </TableRow>
              ) : sortedSuppliers.map(s => (
                <TableRow
                  key={s.id}
                  hover
                  selected={selectedSupplier?.id === s.id}
                  onClick={() => setSelectedSupplier(s)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell><strong>{s.code}</strong></TableCell>
                  <TableCell><strong>{s.name}</strong></TableCell>
                  <TableCell>
                    {s.conditions_summary
                      ? <Chip label={s.conditions_summary} size="small" color="primary" variant="outlined" />
                      : <Chip label="—" size="small" variant="outlined" sx={{ color: 'text.disabled' }} />}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Tooltip title={s.description || ''}>
                      <span>{s.description}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{s.contact_name}</TableCell>
                  <TableCell>{s.contact_email}</TableCell>
                  <TableCell>{s.contact_phone}</TableCell>
                  <TableCell>
                    {s.web && (
                      <Tooltip title={s.web}>
                        <IconButton
                          size="small"
                          component="a"
                          href={s.web.startsWith('http') ? s.web : `https://${s.web}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Language fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(s); }}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteRequest(s.id); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth label="Codigo" name="code" value={form.code || ''} onChange={handleChange} helperText="Editable. Ej: PROV-021" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField fullWidth label="Nombre" name="name" value={form.name || ''} onChange={handleChange} required />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField fullWidth label="CIF" name="cif" value={form.cif || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth select label="Categoria" name="category" value={form.category || ''} onChange={handleChange} SelectProps={{ native: true }}>
                <option value="">-- Seleccionar --</option>
                <option value="Equipos proceso">Equipos proceso</option>
                <option value="Bombas / Intercambiadores">Bombas / Intercambiadores</option>
                <option value="Envasadoras">Envasadoras</option>
                <option value="Etiquetadoras">Etiquetadoras</option>
                <option value="Electrico / Automatizacion">Electrico / Automatizacion</option>
                <option value="Instrumentacion">Instrumentacion</option>
                <option value="Tuberia / Valvuleria">Tuberia / Valvuleria</option>
                <option value="Obra civil">Obra civil</option>
                <option value="Transporte">Transporte</option>
                <option value="Otros">Otros</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={12}>
              <TextField fullWidth label="Web" name="web" value={form.web || ''} onChange={handleChange}
                placeholder="https://www.ejemplo.com" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Descripcion de productos/servicios" name="description"
                value={form.description || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Direccion" name="address" value={form.address || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Ciudad" name="city" value={form.city || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Provincia" name="province" value={form.province || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Pais" name="country" value={form.country || 'Spain'} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField fullWidth label="Contacto" name="contact_name" value={form.contact_name || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Email" name="contact_email" value={form.contact_email || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Telefono" name="contact_phone" value={form.contact_phone || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Notas" name="notes" value={form.notes || ''} onChange={handleChange} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>{editingSupplier ? 'Actualizar' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo de confirmacion de borrado */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar proveedor</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres eliminar <strong>{confirmDelete?.name}</strong>?<br />
            Se eliminarán también sus ofertas y condiciones de pago asociadas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}